import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { GenreRow } from "@/components/GenreRow";
import { FeaturedHero } from "@/components/FeaturedHero";
import { ShareRow } from "@/components/ShareRow";
import { applyOg } from "@/lib/og";
import { API } from "@/lib/api";

export const CATEGORY_ROW_TAGS = new Set([
  "action", "adventure", "animation", "comedy", "crime", "documentary", "drama", "family", "fantasy", "history",
  "horror", "musical", "mystery", "romance", "sci-fi", "thriller", "war", "western",
  "black_cinema", "blaxploitation", "classic", "noir", "anime",
]);
// Anime/Animation hub grids list feature-length titles only (episodes & OVAs have their own row).
export const FEATURE_ONLY_TAGS = new Set(["anime", "animation"]);
const SORT_LABELS = { recent: "Most Recent", watched: "Top Watched" };

const RowSkeleton = () => (
  <div className="mb-14" data-testid="category-rows-skeleton">
    <div className="mb-4 h-8 w-64 animate-pulse bg-ink-surface" />
    <div className="flex gap-4 overflow-hidden py-4">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="aspect-video w-[240px] sm:w-[300px] shrink-0 animate-pulse border border-white/5 bg-ink-surface" />
      ))}
    </div>
  </div>
);

const useCategoryRows = (tag, sort, year) =>
  useQuery({
    queryKey: ["category-rows", tag, sort, year],
    queryFn: async () =>
      (await axios.get(`${API}/categories/${encodeURIComponent(tag)}/rows`, {
        params: { sort, ...(year !== "All" ? { year } : {}) },
      })).data,
    staleTime: 5 * 60 * 1000,
  });

// Genre Spotlight: the homepage hero, fed with this genre's best full-length films.
export const CategorySpotlight = ({ tag, sort, year }) => {
  const { data } = useCategoryRows(tag, sort, year);
  if (!data?.spotlight?.length) return null;
  return (
    <div data-testid={`genre-spotlight-${tag}`}>
      <FeaturedHero slides={data.spotlight} />
    </div>
  );
};

// Share bar + live page meta for the genre (crawlers get the server-rendered /api/og/genre card).
const GenreShare = ({ tag, label }) => {
  const name = label || tag;
  useEffect(() => {
    const origin = window.location.origin;
    return applyOg({
      title: `Stream Top ${name} Movies Free on ReelVault`,
      description: `Free ${String(name).toLowerCase()}: top rated, most watched and new releases on ReelVault.`,
      image: `${origin}/api/og/genre/${encodeURIComponent(tag)}/image.jpg`,
      url: `${origin}/genre/${encodeURIComponent(tag)}`,
      type: "website",
    });
  }, [tag, name]);
  return (
    <div data-testid={`genre-share-${tag}`} className="-mt-2 mb-10">
      <ShareRow
        title={name}
        url={`${window.location.origin}/api/og/genre/${encodeURIComponent(tag)}`}
        text={`Stream top ${name} free on ReelVault!`}
      />
    </div>
  );
};

// Genre page as Netflix-style rows: metric rows, sub-genre intersections and release eras.
export const CategoryRows = ({ tag, sort, year }) => {
  const { data, isLoading } = useCategoryRows(tag, sort, year);
  if (isLoading) return <><RowSkeleton /><RowSkeleton /></>;
  return (
    <div data-testid="category-rows">
      <GenreShare tag={tag} label={data?.short_label} />
      {(data?.rows || []).map((r) => (
        <GenreRow
          key={r.key}
          tag={`cat-${tag}-${r.key}`}
          title={r.title}
          items={r.items}
          sortLabel={r.sort_label || SORT_LABELS[sort]}
        />
      ))}
    </div>
  );
};
